import { type Accessor, createEffect, createSignal, on, onCleanup } from 'solid-js'

import { prefersReducedMotion } from '../../state/appUiStore'
import { currentFast, startupSyncLoading } from '../../state/currentFastStore'

type ActiveFast = NonNullable<ReturnType<typeof currentFast>>

const TARGET_TWEEN_MS = 450

const easeOutCubic = (progress: number) => 1 - (1 - progress) ** 3

/**
 * Returns whether a target change should glide: same fast, a real change, and motion
 * allowed. A new fast, a cleared one, the startup sync, or a hidden tab snaps straight
 * to the value.
 *
 * @param fast - The fast after the change.
 * @param previous - The fast before the change.
 * @param from - The currently displayed target.
 * @returns True when the change should animate.
 */
function shouldTween(
   fast: ActiveFast | null,
   previous: ActiveFast | null | undefined,
   from: number | null,
): fast is ActiveFast {
   return (
      fast !== null &&
      previous?.startTime === fast.startTime &&
      from !== null &&
      from !== fast.duration &&
      !startupSyncLoading() &&
      // Hidden tabs get no animation frames, so a background sync would stall mid-glide.
      document.visibilityState === 'visible' &&
      !prefersReducedMotion()
   )
}

/**
 * Follows the active fast's target duration, but glides to a new value when the same
 * fast's target changes (the +/− buttons, an edit, a sync) instead of jumping, so the ring,
 * the 12-hour marker and the derived figures sweep together.
 *
 * @returns The displayed target in milliseconds, or null without an active fast.
 */
export function createTweenedFastTarget(): Accessor<number | null> {
   const [displayed, setDisplayed] = createSignal<number | null>(
      currentFast()?.duration ?? null,
   )
   let frameId: number | null = null
   let landTimerId: number | null = null

   /**
    * Cancels the running glide's animation frame and its landing fallback, if any.
    */
   const stop = () => {
      if (frameId !== null) {
         cancelAnimationFrame(frameId)
         frameId = null
      }
      if (landTimerId !== null) {
         window.clearTimeout(landTimerId)
         landTimerId = null
      }
   }
   onCleanup(stop)

   createEffect(
      on(
         currentFast,
         (fast, previous) => {
            stop()
            // Starting from the displayed value lets a quick second click retarget smoothly.
            const from = displayed()
            if (!shouldTween(fast, previous, from) || from === null) {
               setDisplayed(fast?.duration ?? null)
               return
            }
            const to = fast.duration
            const startedAt = performance.now()
            /**
             * Advances the glide one frame and schedules the next until it lands.
             *
             * @param frameTime - The animation frame timestamp.
             */
            const step = (frameTime: number) => {
               const progress = Math.min(
                  1,
                  Math.max(0, (frameTime - startedAt) / TARGET_TWEEN_MS),
               )
               setDisplayed(from + (to - from) * easeOutCubic(progress))
               frameId = progress < 1 ? requestAnimationFrame(step) : null
            }
            frameId = requestAnimationFrame(step)
            // Browsers may throttle animation frames (occluded or embedded views), so
            // land on the real value even if the glide never gets to finish.
            landTimerId = window.setTimeout(() => {
               stop()
               setDisplayed(to)
            }, TARGET_TWEEN_MS + 100)
         },
         { defer: true },
      ),
   )

   return displayed
}
